/**
 * useAudioRecorder — Mic recording + Web Audio API RMS analysis + VAD
 * ====================================================================
 * Records audio via MediaRecorder and simultaneously computes
 * real-time RMS amplitude via Web Audio API AnalyserNode.
 *
 * VAD (Voice Activity Detection):
 *   - Silence threshold: 0.015 RMS (low = tolerant of quiet speech)
 *   - Min speech duration: 600ms (prevents background noise triggers)
 *   - Silence timeout: 1800ms (allows natural pauses mid-sentence)
 *   - Max recording: 12s (hard safety net)
 *
 * Returns: {
 *   startRecording, stopRecording,
 *   audioLevel (0..1), stressLevel ('calm'|'normal'|'stress'|'danger'),
 *   isRecording
 * }
 */

import { useState, useRef, useCallback, useEffect } from 'react';

const STRESS_THRESHOLDS = {
  calm:   0.08,
  normal: 0.25,
  stress: 0.55,
};

export default function useAudioRecorder() {
  const [audioLevel, setAudioLevel] = useState(0);
  const [stressLevel, setStressLevel] = useState('calm');
  const [isRecording, setIsRecording] = useState(false);

  const mediaRecorderRef = useRef(null);
  const audioContextRef  = useRef(null);
  const analyserRef      = useRef(null);
  const sourceRef        = useRef(null);
  const streamRef        = useRef(null);
  const rafRef           = useRef(null);
  const chunksRef        = useRef([]);
  const resolveRef       = useRef(null);

  // Start recording — returns a Promise that resolves with {base64, mimeType}
  // VAD: Auto-stops after silenceTimeoutMs of silence (default 1800ms)
  const startRecording = useCallback(async (maxDurationMs = 12000, silenceTimeoutMs = 1800) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 16000,
        },
      });
      streamRef.current = stream;

      // Web Audio API for real-time RMS
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioCtx;

      const source = audioCtx.createMediaStreamSource(stream);
      sourceRef.current = source;

      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 2048;
      analyser.smoothingTimeConstant = 0.8;
      source.connect(analyser);
      analyserRef.current = analyser;

      // VAD state — tuned for natural speech with pauses
      let lastSpeechTime = Date.now();
      let hasSpoken = false;
      let speechStartTime = null;
      const SILENCE_RMS_THRESHOLD = 0.015; // lowered — less false-positive silence
      const MIN_SPEECH_DURATION = 600;     // must speak 600ms before VAD activates

      // Start RMS analysis loop with VAD
      const vadLoop = () => {
        const a = analyserRef.current;
        if (!a) return;

        const dataArray = new Float32Array(a.fftSize);
        a.getFloatTimeDomainData(dataArray);
        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) {
          sum += dataArray[i] * dataArray[i];
        }
        const rms = Math.sqrt(sum / dataArray.length);
        const normalized = Math.min(1, rms * 4);
        setAudioLevel(normalized);

        // Stress classification
        if (normalized > STRESS_THRESHOLDS.stress) setStressLevel('danger');
        else if (normalized > STRESS_THRESHOLDS.normal) setStressLevel('stress');
        else if (normalized > STRESS_THRESHOLDS.calm) setStressLevel('normal');
        else setStressLevel('calm');

        // VAD: Track speech vs silence with MIN_SPEECH_DURATION guard
        if (rms > SILENCE_RMS_THRESHOLD) {
          lastSpeechTime = Date.now();
          if (!hasSpoken) {
            if (!speechStartTime) speechStartTime = Date.now();
            if (Date.now() - speechStartTime > MIN_SPEECH_DURATION) {
              hasSpoken = true;
            }
          }
        } else {
          if (!hasSpoken) speechStartTime = null;
        }

        // Auto-stop: silence for silenceTimeoutMs AFTER user has truly spoken
        if (hasSpoken && (Date.now() - lastSpeechTime > silenceTimeoutMs)) {
          console.log(`[VAD] Silence ${silenceTimeoutMs}ms after ${MIN_SPEECH_DURATION}ms speech — auto-stopping`);
          stopRecording();
          return;
        }

        rafRef.current = requestAnimationFrame(vadLoop);
      };
      rafRef.current = requestAnimationFrame(vadLoop);

      // MediaRecorder for actual audio capture
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';

      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      const promise = new Promise((resolve) => {
        resolveRef.current = resolve;
        recorder.onstop = async () => {
          const blob = new Blob(chunksRef.current, { type: mimeType });
          const base64 = await blobToBase64(blob);
          resolve({ base64: base64.split(',')[1], mimeType: 'audio/webm' });
        };
      });

      recorder.start(200);
      setIsRecording(true);

      // Hard max-stop safety net
      setTimeout(() => {
        if (mediaRecorderRef.current?.state === 'recording') {
          stopRecording();
        }
      }, maxDurationMs);

      return promise;
    } catch (err) {
      console.error('Microphone access denied:', err);
      setIsRecording(false);
      return null;
    }
  }, []);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (audioContextRef.current?.state !== 'closed') {
      audioContextRef.current?.close();
    }
    setIsRecording(false);
    setAudioLevel(0);
  }, []);

  useEffect(() => {
    return () => { stopRecording(); };
  }, [stopRecording]);

  return {
    startRecording,
    stopRecording,
    audioLevel,
    stressLevel,
    isRecording,
  };
}

function blobToBase64(blob) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });
}
